export type ViewType = "Weekly" | "Monthly" | "Quarterly" | "Yearly";

// =============================================
// Returns the Monday of the current calendar week
// at midnight local time.
//
// JavaScript's getDay() returns 0 for Sunday,
// 1 for Monday, ..., 6 for Saturday.
// We normalise Sunday so that it is treated as
// day 7 (the last day of the Mon–Sun week), keeping
// Monday always at offset 0.
// =============================================
export function getCurrentWeekStart(): Date {
  const now = new Date();
  const dayOfWeek = now.getDay();
  const daysSinceMonday = dayOfWeek === 0 ? 6 : dayOfWeek - 1;

  const monday = new Date(now);
  monday.setDate(now.getDate() - daysSinceMonday);
  monday.setHours(0, 0, 0, 0);

  return monday;
}

// =============================================
// Returns the Monday of the previous calendar
// week (7 days before the current week start).
// =============================================
export function getPreviousWeekStart(): Date {
  const current = getCurrentWeekStart();
  const previous = new Date(current);
  previous.setDate(current.getDate() - 7);
  return previous;
}

// =============================================
// Returns the Sunday that closes the week
// beginning on weekStart, at the very end of
// that day (23:59:59.999 local time).
// =============================================
export function getWeekEnd(weekStart: Date): Date {
  const sunday = new Date(weekStart);
  sunday.setDate(weekStart.getDate() + 6);
  sunday.setHours(23, 59, 59, 999);

  return sunday;
}

// =============================================
// Returns the first and last day of the calendar
// month that contains anchor.
// =============================================
export function getMonthRange(anchor: Date): { start: Date; end: Date } {
  const start = new Date(anchor.getFullYear(), anchor.getMonth(), 1, 0, 0, 0, 0);
  const end = new Date(anchor.getFullYear(), anchor.getMonth() + 1, 0, 23, 59, 59, 999);
  return { start, end };
}

// =============================================
// Returns the first and last day of the calendar
// quarter that contains anchor.
// Q1 = Jan–Mar, Q2 = Apr–Jun, Q3 = Jul–Sep, Q4 = Oct–Dec.
// =============================================
export function getQuarterRange(anchor: Date): { start: Date; end: Date } {
  const quarterStartMonth = Math.floor(anchor.getMonth() / 3) * 3;
  const start = new Date(anchor.getFullYear(), quarterStartMonth, 1, 0, 0, 0, 0);
  const end = new Date(anchor.getFullYear(), quarterStartMonth + 3, 0, 23, 59, 59, 999);
  return { start, end };
}

// =============================================
// Returns Jan 1 and Dec 31 of the year that
// contains anchor.
// =============================================
export function getYearRange(anchor: Date): { start: Date; end: Date } {
  const year = anchor.getFullYear();
  const start = new Date(year, 0, 1, 0, 0, 0, 0);
  const end = new Date(year, 11, 31, 23, 59, 59, 999);
  return { start, end };
}

// =============================================
// Returns the first day of the previous calendar
// month relative to anchor.
// =============================================
export function getPreviousMonthAnchor(): Date {
  const now = new Date();
  return new Date(now.getFullYear(), now.getMonth() - 1, 1, 0, 0, 0, 0);
}

// =============================================
// Returns the first day of the previous calendar
// quarter relative to today.
// =============================================
export function getPreviousQuarterAnchor(): Date {
  const now = new Date();
  const quarterStartMonth = Math.floor(now.getMonth() / 3) * 3;
  return new Date(now.getFullYear(), quarterStartMonth - 3, 1, 0, 0, 0, 0);
}

// =============================================
// Returns Jan 1 of the previous calendar year.
// =============================================
export function getPreviousYearAnchor(): Date {
  return new Date(new Date().getFullYear() - 1, 0, 1, 0, 0, 0, 0);
}

// =============================================
// Shifts anchor forward or backward by one unit
// of the given view type.
//
// direction: 1 = forward, -1 = backward.
// =============================================
export function shiftAnchor(anchor: Date, viewType: ViewType, direction: 1 | -1): Date {
  const next = new Date(anchor);

  switch (viewType) {
    case "Weekly":
      next.setDate(anchor.getDate() + direction * 7);
      break;
    case "Monthly":
      next.setMonth(anchor.getMonth() + direction);
      break;
    case "Quarterly":
      next.setMonth(anchor.getMonth() + direction * 3);
      break;
    case "Yearly":
      next.setFullYear(anchor.getFullYear() + direction);
      break;
  }

  return next;
}

// =============================================
// Returns true when anchor falls within the
// current period for the given view type.
// =============================================
export function isAnchorCurrentPeriod(anchor: Date, viewType: ViewType): boolean {
  const now = new Date();

  switch (viewType) {
    case "Weekly":
      return anchor.toDateString() === getCurrentWeekStart().toDateString();
    case "Monthly":
      return (
        anchor.getFullYear() === now.getFullYear() &&
        anchor.getMonth() === now.getMonth()
      );
    case "Quarterly":
      return (
        anchor.getFullYear() === now.getFullYear() &&
        Math.floor(anchor.getMonth() / 3) === Math.floor(now.getMonth() / 3)
      );
    case "Yearly":
      return anchor.getFullYear() === now.getFullYear();
  }
}

// =============================================
// Formats a week range as a human-readable label.
//
// Examples:
//   Same month:       "Apr 28 – May 4, 2026"
//   Cross-month:      "Mar 30 – Apr 5, 2026"
//   Same month/year:  "Apr 7 – Apr 13, 2026"
//
// The year is appended only to the end date to
// keep the label compact while remaining unambiguous.
// =============================================
export function formatWeekRange(weekStart: Date, weekEnd: Date): string {
  const startMonth = weekStart.toLocaleString("default", { month: "short" });
  const startDay = weekStart.getDate();

  const endMonth = weekEnd.toLocaleString("default", { month: "short" });
  const endDay = weekEnd.getDate();
  const endYear = weekEnd.getFullYear();

  const startLabel =
    startMonth === endMonth
      ? `${startMonth} ${startDay}`
      : `${startMonth} ${startDay}`;

  return `${startLabel} – ${endMonth} ${endDay}, ${endYear}`;
}

// =============================================
// Formats a month anchor as "May 2026".
// =============================================
export function formatMonthLabel(anchor: Date): string {
  const month = anchor.toLocaleString("default", { month: "long" });
  return `${month} ${anchor.getFullYear()}`;
}

// =============================================
// Formats a quarter anchor as "Q2 2026".
// =============================================
export function formatQuarterLabel(anchor: Date): string {
  const quarter = Math.floor(anchor.getMonth() / 3) + 1;
  return `Q${quarter} ${anchor.getFullYear()}`;
}

// =============================================
// Formats a year anchor as "2026".
// =============================================
export function formatYearLabel(anchor: Date): string {
  return String(anchor.getFullYear());
}

// =============================================
// Converts a Date to an ISO 8601 date-only string
// (e.g. "2026-04-28") using local time components
// so the date matches the calendar week displayed
// to the user regardless of timezone offset.
// =============================================
export function toLocalDateString(date: Date): string {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");

  return `${year}-${month}-${day}`;
}
